import { Device, FaultData, Trip } from "@fairfleet/geotab";
import { QueryDeviceData } from "@/models/QueryDiagnosticDeviceData";

const TOP_LIST_SIZE = 5;

export interface FleetDeviceSpeed {
  deviceId: string;
  deviceName: string;
  maxSpeedKmh: number;
}

export interface FleetFaultSummary {
  diagnosticId: string;
  diagnosticName: string;
  count: number;
}

export interface FleetSummary {
  totalDevices: number;
  activeDevices: number;
  utilizationPercent: number | null;
  fleetMaxSpeedKmh: number | null;
  topSpeeds: FleetDeviceSpeed[];
  combustionFuelUsedLiters: number;
  combustionVehicleCount: number;
  fuelCellVehicleCount: number;
  openFaultCount: number;
  topFaults: FleetFaultSummary[];
}

export interface SummarizeFleetInput {
  devices: Device[];
  trips: Array<Pick<Trip, "device">>;
  /** Engine road speed readings per device id. */
  speedData: QueryDeviceData;
  /** Trip fuel used readings per device id. */
  fuelData: QueryDeviceData;
  faultData: Array<Pick<FaultData, "diagnostic" | "count">>;
  fuelCellDeviceIds: string[];
  faultDiagnosticsById: Record<string, { name?: string } | undefined>;
}

export function buildMaxSpeedByDevice(readingsByDevice: QueryDeviceData) {
  const maxSpeedByDevice = new Map<string, number>();

  for (const [deviceId, readings] of Object.entries(readingsByDevice)) {
    for (const { data } of readings) {
      if (!Number.isFinite(data)) continue;
      const current = maxSpeedByDevice.get(deviceId) ?? 0;
      if (data > current) {
        maxSpeedByDevice.set(deviceId, data);
      }
    }
  }

  return maxSpeedByDevice;
}

/** Pure aggregation of the dashboard figures from the raw query results. */
export function summarizeFleet({
  devices,
  trips,
  speedData,
  fuelData,
  faultData,
  fuelCellDeviceIds,
  faultDiagnosticsById,
}: SummarizeFleetInput): FleetSummary {
  const deviceIds = devices.map(device => device.id);
  const deviceById = new Map<string, Device>(devices.map(device => [device.id, device]));
  const fuelCellIdSet = new Set(fuelCellDeviceIds);
  const isFuelCellDevice = (deviceId: string) => fuelCellIdSet.has(deviceId);

  // Utilization: share of fleet devices with at least one trip in the period.
  const activeDeviceIds = new Set(
    trips.map(trip => trip.device?.id).filter((id): id is string => !!id && deviceById.has(id)),
  );
  const utilizationPercent =
    deviceIds.length > 0 ? (activeDeviceIds.size / deviceIds.length) * 100 : null;

  // Top speeds: max value per device, from the engine road speed diagnostic.
  const maxSpeedByDevice = buildMaxSpeedByDevice(speedData);
  const topSpeeds: FleetDeviceSpeed[] = [...maxSpeedByDevice.entries()]
    .map(([deviceId, maxSpeedKmh]) => ({
      deviceId,
      deviceName: deviceById.get(deviceId)?.name ?? deviceId,
      maxSpeedKmh,
    }))
    .sort((a, b) => b.maxSpeedKmh - a.maxSpeedKmh)
    .slice(0, TOP_LIST_SIZE);
  const fleetMaxSpeedKmh =
    maxSpeedByDevice.size > 0
      ? [...maxSpeedByDevice.values()].reduce((max, value) => Math.max(max, value))
      : null;

  // Fuel consumption: sum combustion fuel used per device, separated from fuel-cell (H2) vehicles.
  const fuelSumByDevice = new Map<string, number>();
  const combustionDeviceIds = new Set<string>();
  for (const [deviceId, readings] of Object.entries(fuelData)) {
    if (isFuelCellDevice(deviceId)) continue;
    for (const { data } of readings) {
      if (!Number.isFinite(data)) continue;
      fuelSumByDevice.set(deviceId, (fuelSumByDevice.get(deviceId) ?? 0) + data);
      combustionDeviceIds.add(deviceId);
    }
  }
  const combustionFuelUsedLiters = [...fuelSumByDevice.values()].reduce(
    (sum, value) => sum + value,
    0,
  );
  const fuelCellVehicleCount = devices.filter(device => isFuelCellDevice(device.id)).length;

  // Fault codes: number of open device x code pairs (useLatestFaultData yields one row per pair,
  // its `count` is the occurrence count), and top diagnostics by occurrences.
  const openFaultCount = faultData.length;
  const countByDiagnosticId = new Map<string, number>();
  faultData.forEach(fault => {
    const diagnosticId = fault.diagnostic?.id;
    if (!diagnosticId) return;
    countByDiagnosticId.set(
      diagnosticId,
      (countByDiagnosticId.get(diagnosticId) ?? 0) + (fault.count ?? 0),
    );
  });
  const topFaults: FleetFaultSummary[] = [...countByDiagnosticId.entries()]
    .map(([diagnosticId, count]) => ({
      diagnosticId,
      diagnosticName: faultDiagnosticsById[diagnosticId]?.name ?? diagnosticId,
      count,
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, TOP_LIST_SIZE);

  return {
    totalDevices: deviceIds.length,
    activeDevices: activeDeviceIds.size,
    utilizationPercent,
    fleetMaxSpeedKmh,
    topSpeeds,
    combustionFuelUsedLiters,
    combustionVehicleCount: combustionDeviceIds.size,
    fuelCellVehicleCount,
    openFaultCount,
    topFaults,
  };
}
